/**
 * GlobalColorEditor
 *
 * UI panel for editing the site's brand colors globally.
 * Changes primary and secondary colors across all pages.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Palette, RefreshCw, Check, X, Undo2, Sun, Moon } from 'lucide-react';
import { switchTheme } from '../../api/editor';
import { apiGet, apiPatch } from '../../api/client';
import { normalizeHex, normalizeHexWhileTyping } from '../../utils/hex-color';

// =============================================================================
// TYPES
// =============================================================================

interface ColorEditorProps {
  websiteId: string;
  onColorsChanged?: () => void;
}

interface ColorResponse {
  success: boolean;
  currentColors: {
    primary: string;
    secondary: string;
  };
  currentTheme?: 'light' | 'dark';
  onboardingColors?: {
    primary: string | null;
    secondary: string | null;
  };
  overrides?: {
    footerBg: string | null;
    navBg: string | null;
  };
  /** What the footer would be if no override were set */
  derivedFooterBg?: string;
  hasUnpublishedChanges?: boolean;
}

interface UpdateColorResponse {
  success: boolean;
  message?: string;
  error?: string;
  pagesUpdated?: number;
}

// =============================================================================
// COLOR UTILITIES
// =============================================================================

function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return { h: 0, s: 0, l: 0 };

  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

function hslToHex(h: number, s: number, l: number): string {
  h /= 360;
  s /= 100;
  l /= 100;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (x: number) => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function lightenColor(hex: string, amount: number): string {
  const hsl = hexToHsl(hex);
  const newL = Math.min(100, hsl.l + amount);
  return hslToHex(hsl.h, hsl.s, newL);
}

function darkenColor(hex: string, amount: number): string {
  const hsl = hexToHsl(hex);
  const newL = Math.max(0, hsl.l - amount);
  return hslToHex(hsl.h, hsl.s, newL);
}

// =============================================================================
// COMPONENT
// =============================================================================

export function GlobalColorEditor({ websiteId, onColorsChanged }: ColorEditorProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Applied colors (what's saved in staging)
  const [appliedPrimary, setAppliedPrimary] = useState('#3AABE8');
  const [appliedSecondary, setAppliedSecondary] = useState('#F9B03F');

  // Edited colors (what user is currently editing)
  const [editedPrimary, setEditedPrimary] = useState('#3AABE8');
  const [editedSecondary, setEditedSecondary] = useState('#F9B03F');

  // History for undo (stores previous applied states)
  const [history, setHistory] = useState<Array<{ primary: string; secondary: string }>>([]);

  // Explicit surface overrides ('' = use the derived colour)
  const [footerBgOverride, setFooterBgOverride] = useState('');
  const [navBgOverride, setNavBgOverride] = useState('');
  const [derivedFooterBg, setDerivedFooterBg] = useState<string>('#1c1917');
  const [savingSurfaces, setSavingSurfaces] = useState(false);
  // What the server currently has. Compared against the edited value to decide
  // whether a save is needed — comparing against the edited value itself never
  // fires, because onChange has already updated it by the time blur runs.
  const savedFooterBg = useRef('');
  const savedNavBg = useRef('');

  // Original onboarding colors (for reset)
  const [onboardingPrimary, setOnboardingPrimary] = useState<string | null>(null);
  const [onboardingSecondary, setOnboardingSecondary] = useState<string | null>(null);

  // Has unpublished changes indicator
  const [hasUnpublishedChanges, setHasUnpublishedChanges] = useState(false);

  // Theme mode state
  const [currentTheme, setCurrentTheme] = useState<'light' | 'dark'>('light');
  const [switchingTheme, setSwitchingTheme] = useState(false);

  // Track if fetching has happened
  const hasFetched = useRef(false);

  // Track if user has unsaved edits (before Apply)
  const hasUnsavedEdits =
    editedPrimary !== appliedPrimary || editedSecondary !== appliedSecondary;

  // Fetch current colors on mount (only once)
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchColors = async () => {
      try {
        const data = await apiGet<ColorResponse>(`/api/editor/${websiteId}/colors`);

        if (data.success && data.currentColors) {
          setAppliedPrimary(data.currentColors.primary);
          setAppliedSecondary(data.currentColors.secondary);
          setEditedPrimary(data.currentColors.primary);
          setEditedSecondary(data.currentColors.secondary);
        }

        if (data.onboardingColors) {
          setOnboardingPrimary(data.onboardingColors.primary);
          setOnboardingSecondary(data.onboardingColors.secondary);
        }

        if (data.currentTheme) {
          setCurrentTheme(data.currentTheme);
        }

        setFooterBgOverride(data.overrides?.footerBg || '');
        setNavBgOverride(data.overrides?.navBg || '');
        savedFooterBg.current = data.overrides?.footerBg || '';
        savedNavBg.current = data.overrides?.navBg || '';
        if (data.derivedFooterBg) setDerivedFooterBg(data.derivedFooterBg);

        setHasUnpublishedChanges(data.hasUnpublishedChanges || false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load colors');
      } finally {
        setLoading(false);
      }
    };

    fetchColors();
  }, [websiteId]);

  // Apply color changes
  const handleApplyColors = useCallback(async () => {
    if (!hasUnsavedEdits) return;

    const primary = normalizeHex(editedPrimary);
    const secondary = normalizeHex(editedSecondary);
    if (!primary || !secondary) {
      setError('Enter each color as a hex code, like #3AABE8.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const data = await apiPatch<UpdateColorResponse>(
        `/api/editor/${websiteId}/colors`,
        { primaryColor: primary, secondaryColor: secondary }
      );

      if (!data.success) {
        throw new Error(data.error || data.message || 'Failed to update colors');
      }

      // Save current applied colors to history (for undo)
      setHistory((prev) => [...prev.slice(-9), { primary: appliedPrimary, secondary: appliedSecondary }]);

      // Update applied to match edited
      setEditedPrimary(primary);
      setEditedSecondary(secondary);
      setAppliedPrimary(primary);
      setAppliedSecondary(secondary);
      setHasUnpublishedChanges(true);
      setSuccess(`Colors applied! ${data.pagesUpdated || 0} page(s) updated. Publish to deploy.`);

      // Notify parent to refresh preview
      onColorsChanged?.();

      // Clear success after 4s
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save colors');
    } finally {
      setSaving(false);
    }
  }, [websiteId, editedPrimary, editedSecondary, hasUnsavedEdits, appliedPrimary, appliedSecondary, onColorsChanged]);

  // Cancel unsaved edits (revert to applied)
  const handleCancelEdits = useCallback(() => {
    setEditedPrimary(appliedPrimary);
    setEditedSecondary(appliedSecondary);
  }, [appliedPrimary, appliedSecondary]);

  // Undo last applied change
  const handleUndo = useCallback(async () => {
    if (history.length === 0) return;

    const previous = history[history.length - 1];
    setSaving(true);
    setError(null);

    try {
      const data = await apiPatch<UpdateColorResponse>(
        `/api/editor/${websiteId}/colors`,
        { primaryColor: previous.primary, secondaryColor: previous.secondary }
      );

      // apiPatch throws on a non-OK response, so only the body's own success
      // flag is left to check.
      if (!data.success) {
        throw new Error(data.error || 'Failed to undo');
      }

      // Remove last item from history
      setHistory((prev) => prev.slice(0, -1));

      // Restore previous colors
      setAppliedPrimary(previous.primary);
      setAppliedSecondary(previous.secondary);
      setEditedPrimary(previous.primary);
      setEditedSecondary(previous.secondary);

      setSuccess('Undone! Previous colors restored.');
      onColorsChanged?.();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to undo');
    } finally {
      setSaving(false);
    }
  }, [history, websiteId, onColorsChanged]);

  // Save the nav/footer overrides. Sent on their own so applying them does not
  // require re-applying primary/secondary.
  const saveSurfaceOverrides = useCallback(
    async (next: { footerBgColor?: string; navBgColor?: string }) => {
      setSavingSurfaces(true);
      setError(null);
      try {
        const data = await apiPatch<UpdateColorResponse>(
          `/api/editor/${websiteId}/colors`,
          { primaryColor: appliedPrimary, secondaryColor: appliedSecondary, ...next }
        );
        if (!data.success) {
          throw new Error(data.error || data.message || 'Failed to update colors');
        }
        if (next.footerBgColor !== undefined) savedFooterBg.current = next.footerBgColor;
        if (next.navBgColor !== undefined) savedNavBg.current = next.navBgColor;
        setHasUnpublishedChanges(true);
        setSuccess('Updated');
        setTimeout(() => setSuccess(null), 1800);
        onColorsChanged?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update');
      } finally {
        setSavingSurfaces(false);
      }
    },
    [websiteId, appliedPrimary, appliedSecondary, onColorsChanged]
  );

  // Debounced commit for the colour pickers.
  const commitTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const commitLater = useCallback(
    (
      key: string,
      value: string,
      send: (v: string) => void,
      saved: { current: string }
    ) => {
      clearTimeout(commitTimers.current[key]);
      commitTimers.current[key] = setTimeout(() => {
        if (value !== saved.current) send(value);
      }, 500);
    },
    []
  );

  // Reset to original onboarding colors
  const handleResetToOnboarding = useCallback(() => {
    if (onboardingPrimary) setEditedPrimary(onboardingPrimary);
    if (onboardingSecondary) setEditedSecondary(onboardingSecondary);
  }, [onboardingPrimary, onboardingSecondary]);

  // Switch theme (light ↔ dark)
  const handleThemeSwitch = useCallback(async (targetMode: 'light' | 'dark') => {
    if (targetMode === currentTheme || switchingTheme) return;

    setSwitchingTheme(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await switchTheme(websiteId, targetMode);

      if (!result.success) {
        throw new Error(result.error || result.message || 'Failed to switch theme');
      }

      setCurrentTheme(targetMode);
      setHasUnpublishedChanges(true);
      setSuccess(`Switched to ${targetMode} mode! ${result.pagesUpdated || 0} page(s) updated.`);

      // Notify parent to refresh preview
      onColorsChanged?.();

      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to switch theme');
    } finally {
      setSwitchingTheme(false);
    }
  }, [websiteId, currentTheme, switchingTheme, onColorsChanged]);

  if (loading) {
    return (
      <div className="p-4 text-center text-gray-500 text-sm">
        Loading colors...
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Palette className="w-4 h-4 text-pink-500" />
            <span className="font-medium text-sm text-gray-900">Brand Colors</span>
          </div>
          {hasUnpublishedChanges && (
            <span className="text-xs text-amber-600 font-medium">Unpublished</span>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-0.5">
          Changes apply to staging. Publish to deploy.
        </p>
      </div>

      <div className="p-4 space-y-4">
        {/* Error message */}
        {error && (
          <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Success message */}
        {success && (
          <div className="p-2 bg-green-50 border border-green-200 rounded text-sm text-green-700 flex items-center gap-2">
            <Check className="w-4 h-4" />
            {success}
          </div>
        )}

        {/* Theme Mode Toggle */}
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            Theme Mode
          </label>
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => handleThemeSwitch('light')}
              disabled={switchingTheme}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${
                currentTheme === 'light'
                  ? 'bg-amber-50 text-amber-700 border-r border-gray-200'
                  : 'bg-white text-gray-500 hover:bg-gray-50 border-r border-gray-200'
              }`}
            >
              <Sun className="w-4 h-4" />
              Light
            </button>
            <button
              onClick={() => handleThemeSwitch('dark')}
              disabled={switchingTheme}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${
                currentTheme === 'dark'
                  ? 'bg-indigo-50 text-indigo-700'
                  : 'bg-white text-gray-500 hover:bg-gray-50'
              }`}
            >
              {switchingTheme ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Moon className="w-4 h-4" />
              )}
              Dark
            </button>
          </div>
        </div>

        {/* Primary Color */}
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            Primary Color
          </label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={normalizeHex(editedPrimary) ?? '#000000'}
              onChange={(e) => setEditedPrimary(e.target.value)}
              className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
            />
            <input
              type="text"
              value={editedPrimary}
              onChange={(e) => setEditedPrimary(normalizeHexWhileTyping(e.target.value))}
              onBlur={() => setEditedPrimary(normalizeHex(editedPrimary) ?? editedPrimary)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm font-mono"
              placeholder="#3AABE8"
            />
          </div>
          {/* Preview shades */}
          <div className="flex gap-1 mt-2">
            <div
              className="flex-1 h-6 rounded"
              style={{ background: lightenColor(normalizeHex(editedPrimary) ?? '#000000', 20) }}
              title="Light"
            />
            <div
              className="flex-1 h-6 rounded"
              style={{ background: normalizeHex(editedPrimary) ?? 'transparent' }}
              title="Primary"
            />
            <div
              className="flex-1 h-6 rounded"
              style={{ background: darkenColor(normalizeHex(editedPrimary) ?? '#000000', 20) }}
              title="Dark"
            />
          </div>
        </div>

        {/* Secondary Color */}
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            Secondary Color
          </label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={normalizeHex(editedSecondary) ?? '#000000'}
              onChange={(e) => setEditedSecondary(e.target.value)}
              className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
            />
            <input
              type="text"
              value={editedSecondary}
              onChange={(e) => setEditedSecondary(normalizeHexWhileTyping(e.target.value))}
              onBlur={() => setEditedSecondary(normalizeHex(editedSecondary) ?? editedSecondary)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm font-mono"
              placeholder="#F9B03F"
            />
          </div>
          {/* Preview shades */}
          <div className="flex gap-1 mt-2">
            <div
              className="flex-1 h-6 rounded"
              style={{ background: lightenColor(normalizeHex(editedSecondary) ?? '#000000', 20) }}
              title="Light"
            />
            <div
              className="flex-1 h-6 rounded"
              style={{ background: normalizeHex(editedSecondary) ?? 'transparent' }}
              title="Secondary"
            />
            <div
              className="flex-1 h-6 rounded"
              style={{ background: darkenColor(normalizeHex(editedSecondary) ?? '#000000', 20) }}
              title="Dark"
            />
          </div>
        </div>

        {/* Color preview (combined) */}
        <div className="pt-2 border-t border-gray-100">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            Preview
          </label>
          <div className="flex gap-2">
            <button
              className="flex-1 px-4 py-2 rounded-lg text-white text-sm font-medium"
              style={{ background: normalizeHex(editedPrimary) ?? 'transparent' }}
            >
              Primary Button
            </button>
            <button
              className="flex-1 px-4 py-2 rounded-lg text-sm font-medium border-2"
              style={{
                borderColor: normalizeHex(editedPrimary) ?? 'transparent',
                color: normalizeHex(editedPrimary) ?? 'inherit',
              }}
            >
              Secondary
            </button>
          </div>
          <div
            className="mt-2 p-3 rounded-lg text-white text-xs text-center"
            style={{ background: normalizeHex(editedSecondary) ?? 'transparent' }}
          >
            Accent / Highlight
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          {hasUnsavedEdits && (
            <button
              onClick={handleCancelEdits}
              className="flex-1 px-3 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-1.5"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
          )}
          <button
            onClick={handleApplyColors}
            disabled={!hasUnsavedEdits || saving}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg flex items-center justify-center gap-1.5 ${
              hasUnsavedEdits && !saving
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Applying...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Apply Colors
              </>
            )}
          </button>
        </div>

        {/* Header / footer surface overrides */}
        <div className="pt-4 mt-4 border-t border-gray-200 space-y-3">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Surfaces</p>
            <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">
              Header and footer are derived from the primary colour. Set one explicitly when the
              derived shade doesn't suit — a light primary darkens into an unrelated hue.
            </p>
          </div>

          {([
            {
              key: 'nav' as const,
              label: 'Header / nav',
              value: navBgOverride,
              set: setNavBgOverride,
              send: (v: string) => saveSurfaceOverrides({ navBgColor: v }),
              saved: savedNavBg,
              fallbackLabel: 'theme default',
              fallbackSwatch: currentTheme === 'dark' ? '#111827' : '#ffffff',
            },
            {
              key: 'footer' as const,
              label: 'Footer',
              value: footerBgOverride,
              set: setFooterBgOverride,
              send: (v: string) => saveSurfaceOverrides({ footerBgColor: v }),
              saved: savedFooterBg,
              fallbackLabel: 'derived',
              fallbackSwatch: derivedFooterBg,
            },
          ]).map((row) => (
            <div key={row.key}>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-gray-600">{row.label}</label>
                {row.value && (
                  <button
                    onClick={() => { row.set(''); if (row.saved.current !== '') row.send(''); }}
                    disabled={savingSurfaces}
                    className="text-[11px] text-gray-400 hover:text-gray-600 disabled:opacity-50"
                  >
                    Use {row.fallbackLabel}
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={row.value || row.fallbackSwatch}
                  onChange={(e) => {
                    const v = e.target.value;
                    row.set(v);
                    // Native colour inputs fire onChange continuously while
                    // dragging and don't reliably blur, so commit on a debounce
                    // rather than on blur — one save per pick, not per pixel.
                    commitLater(row.key, v, row.send, row.saved);
                  }}
                  disabled={savingSurfaces}
                  className="w-9 h-9 rounded border border-gray-300 cursor-pointer disabled:opacity-50"
                  aria-label={`${row.label} background`}
                />
                <input
                  type="text"
                  value={row.value}
                  placeholder={`${row.fallbackSwatch} (${row.fallbackLabel})`}
                  onChange={(e) => row.set(e.target.value)}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v !== row.saved.current && (v === '' || /^#[0-9a-fA-F]{6}$/.test(v))) {
                      row.send(v);
                    }
                  }}
                  disabled={savingSurfaces}
                  className="flex-1 min-w-0 px-2 py-1.5 text-sm font-mono border border-gray-300 rounded disabled:opacity-50"
                />
              </div>
            </div>
          ))}

          {savingSurfaces && (
            <p className="text-[11px] text-gray-400 flex items-center gap-1">
              <RefreshCw className="w-3 h-3 animate-spin" /> Rebuilding pages…
            </p>
          )}
        </div>

        {/* Undo button - show when there's history */}
        {history.length > 0 && !hasUnsavedEdits && (
          <button
            onClick={handleUndo}
            disabled={saving}
            className="w-full px-3 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-1.5"
          >
            <Undo2 className="w-4 h-4" />
            Undo Last Change
          </button>
        )}

        {/* Reset to onboarding colors */}
        {(onboardingPrimary || onboardingSecondary) && (
          <button
            onClick={handleResetToOnboarding}
            className="w-full text-xs text-gray-500 hover:text-gray-700 py-1"
          >
            Reset to original onboarding colors
          </button>
        )}
      </div>
    </div>
  );
}
